import { FlightOffer, FlightSegment, TravelerPricing } from "../types/flight";

export const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

export const formatDuration = (duration: string): string => {
    // Parse ISO 8601 duration (e.g., "PT2H35M")
    const matches = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!matches) return duration;

    const hours = matches[1] || "0";
    const minutes = matches[2] || "0";

    return `${hours}h ${minutes}m`;
};

export const formatCurrency = (
    amount: string | number,
    currency = "USD"
): string => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numAmount);
};

export const calculateLayoverTime = (
    arrival: string,
    nextDeparture: string
): string => {
    const arrivalTime = new Date(arrival).getTime();
    const departureTime = new Date(nextDeparture).getTime();
    const diffMs = departureTime - arrivalTime;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
};

export const getStopsText = (segments: FlightSegment[]): string => {
    const stops = segments.length - 1;
    if (stops === 0) return "non stop";
    if (stops === 1) return "1 stop";
    return `${stops} stops`;
};

export const getCarrierName = (code: string): string => {
    // This would normally come from the API dictionaries
    const carriers: { [key: string]: string } = {
        PR: "Philippine Airlines",
        CX: "Cathay Pacific",
        SQ: "Singapore Airlines",
        // Add more as needed
    };
    return carriers[code] || code;
};

export const getFareBrands = (offer: FlightOffer): string[] => {
    const brands = new Set<string>();

    offer.travelerPricings.forEach((pricing: TravelerPricing) => {
        pricing.fareDetailsBySegment.forEach((detail) => {
            if (detail.brandedFareLabel) {
                brands.add(detail.brandedFareLabel);
            }
        });
    });

    // Return common Philippine Airlines fare types if no brands found
    if (brands.size === 0) {
        return ["Economy", "Premium Economy", "Business"];
    }

    return Array.from(brands);
};

export const getLowestFare = (offer: FlightOffer): string => {
    // Return the base price as the lowest fare
    return offer.price.base;
};

export const groupFlightsByDate = (
    offers: FlightOffer[]
): Map<string, FlightOffer[]> => {
    const grouped = new Map<string, FlightOffer[]>();

    offers.forEach((offer) => {
        const departureDate =
            offer.itineraries[0].segments[0].departure.at.split("T")[0];
        const existing = grouped.get(departureDate) || [];
        existing.push(offer);
        grouped.set(departureDate, existing);
    });

    return grouped;
};
